import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const PLACE_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const PLACE_DETAILS_URL = 'https://places.googleapis.com/v1/places';

export type PlacePrediction = NonNullable<google.maps.places.AutocompleteSuggestion>;
import { defaults } from '@sendpayments/js-utils';

export type Address = {
  streetAddressUnitNumber?: string;
  streetAddressName?: string;
  streetAddressType?: string;
  streetAddressNumber?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
};

/**
 * Return display-able values of suggestions.
 */
export const getDisplayValueFromId = (id: string, data: { name: string; value: string }[]) => {
  const item = data.find((suggestion) => suggestion.value === id);
  if (!item) {
    return '';
  }
  return `${item.name}`;
};
/**
 * Takes a place result and splits it out into the data we need
 */
export const getAddressFromGoogleApi = (addressComponents: google.maps.places.AddressComponent[]): Address => {
  const data: Address = {};
  for (let i = 0, iLen = addressComponents?.length; i < iLen; i++) {
    if (addressComponents[i].types.includes('route')) {
      data.streetAddressName = addressComponents[i].longText || '';
      defaults.streetTypes.forEach((element) => {
        if (addressComponents?.[i].longText?.includes(element[0])) {
          // eslint-disable-next-line prefer-destructuring
          data.streetAddressType = element[0];
          data.streetAddressName = addressComponents[i].longText?.replace(element[0], '').trim();
        }
      });
    }
    if (addressComponents[i].types.includes('street_number')) {
      data.streetAddressNumber = addressComponents[i].shortText || '';
    }
    if (addressComponents[i].types.includes('locality')) {
      data.city = addressComponents[i].shortText || '';
    }
    if (addressComponents[i].types.includes('administrative_area_level_1')) {
      data.state = addressComponents[i].shortText || '';
    }
    if (addressComponents[i].types.includes('postal_code')) {
      data.postcode = addressComponents[i].shortText || '';
    }
    if (addressComponents[i].types.includes('country')) {
      data.country = addressComponents[i].longText || '';
    }
  }
  return data;
};

type UseGoogleMapsParams = {
  sessionToken?: string;
  debounce?: number;
};

export const useGoogleMaps = (props: UseGoogleMapsParams) => {
  const [value, setValue] = useState('');
  const [data, setData] = useState<{ name: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const sessionTokenRef = useRef<string | null>(props.sessionToken || null);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!input.trim()) {
      setData([]);
      return;
    }

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = crypto.randomUUID();
    }

    setLoading(true);
    try {
      const response = await axios.post<{ suggestions: google.maps.places.AutocompleteSuggestion[] }>(
        PLACE_AUTOCOMPLETE_URL,
        {
          input,
        },
        {
          params: {
            sessionToken: sessionTokenRef.current,
          },
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': process.env.GOOGLE_API_KEY,
          },
        },
      );

      const predictions = response.data.suggestions || [];
      const formatted = predictions.map(({ placePrediction }) => ({
        name: placePrediction?.text.text || '',
        value: placePrediction?.placeId || '',
      }));
      setData(formatted);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const getAddressDetailsFromPlaceId = async (placeId: string): Promise<Address> => {
    let address: Address = {
      streetAddressUnitNumber: '',
      streetAddressName: '',
      streetAddressNumber: '',
      streetAddressType: '',
      city: '',
      state: '',
      postcode: '',
      country: '',
    };
    if (!placeId) {
      return address;
    }

    const response = await axios.get<google.maps.places.Place>(`${PLACE_DETAILS_URL}/${placeId}`, {
      params: {
        sessionToken: sessionTokenRef.current,
      },
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': process.env.GOOGLE_API_KEY,
        'X-Goog-FieldMask': 'id,addressComponents',
      },
    });

    const components = response.data.addressComponents || [];

    address = getAddressFromGoogleApi(components);

    return address;
  };

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(value);
    }, props.debounce || 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value, fetchSuggestions]);

  return {
    value,
    setValue,
    data,
    loading,
    getAddressDetailsFromPlaceId,
  };
};
